import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useSupabase } from '../../contexts/SupabaseContext';
import { previewSaleEdit, saveSaleEdit } from '../../utils/stockSales';

const formatINR = (amount) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
}).format(amount);

const numberInputSx = {
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
};

// Edit a recorded stock sale (stock_sales row `saleId`). Loads the sale and the
// stock's current holdings, previews the new net amount and profit/loss, and saves
// via saveSaleEdit (which adjusts holdings, oldest shares first, if units change).
// onSaved({ unitsChanged, unitsDifference }) is called after a successful save.
const EditSaleDialog = ({ open, saleId, onClose, onSaved }) => {
  const { supabase, user } = useSupabase();
  const [sale, setSale] = useState(null);
  const [lots, setLots] = useState([]);
  const [loadingSale, setLoadingSale] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [date, setDate] = useState(null);
  const [price, setPrice] = useState('');
  const [units, setUnits] = useState('');
  const [charges, setCharges] = useState('0');

  useEffect(() => {
    if (!open || !saleId || !user) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingSale(true);
      setError(null);
      try {
        const { data: saleRow, error: saleError } = await supabase
          .from('stock_sales')
          .select('*')
          .eq('id', saleId)
          .single();
        if (saleError) throw saleError;
        const { data: lotRows, error: lotsError } = await supabase
          .from('stocks_investments')
          .select('*')
          .eq('user_id', user.id)
          .eq('name', saleRow.stock_name);
        if (lotsError) throw lotsError;
        if (cancelled) return;
        setSale(saleRow);
        setLots(lotRows || []);
        setDate(saleRow.sale_date ? new Date(`${String(saleRow.sale_date).slice(0, 10)}T00:00:00`) : new Date());
        setPrice(String(saleRow.sale_price ?? ''));
        setUnits(String(saleRow.units_sold ?? ''));
        setCharges(String(saleRow.charges ?? 0));
      } catch (err) {
        if (!cancelled) setError(`Couldn't load this sale: ${err.message || 'Unknown error'}`);
      } finally {
        if (!cancelled) setLoadingSale(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, saleId, user, supabase]);

  const handleClose = () => {
    if (saving) return;
    setSale(null);
    setError(null);
    onClose();
  };

  const preview = useMemo(() => previewSaleEdit(sale, lots, {
    units: parseFloat(units),
    price: parseFloat(price),
    charges: parseFloat(charges) || 0,
  }), [sale, lots, units, price, charges]);

  const handleSave = async () => {
    const newUnits = parseFloat(units);
    const newPrice = parseFloat(price);
    const newCharges = parseFloat(charges) || 0;
    if (!date || Number.isNaN(new Date(date).getTime())) {
      setError('Please enter a valid sale date.');
      return;
    }
    if (!(newUnits > 0) || !(newPrice > 0)) {
      setError('Units sold and sale price must be more than 0.');
      return;
    }
    if (newCharges < 0) {
      setError('Charges cannot be negative.');
      return;
    }
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await saveSaleEdit({
        supabase,
        userId: user.id,
        saleId,
        edits: { units: newUnits, price: newPrice, charges: newCharges, date },
      });
      setSale(null);
      onSaved(result);
    } catch (err) {
      setError(`Failed to update sale: ${err.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs" aria-labelledby="edit-sale-title">
      <DialogTitle id="edit-sale-title" sx={{ fontWeight: 600 }}>
        Edit Sale{sale ? `: ${sale.stock_name}` : ''}
      </DialogTitle>
      <DialogContent>
        {loadingSale || (!sale && !error) ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}
              {sale && (
                <>
                  <DatePicker
                    label="Sale Date"
                    value={date}
                    onChange={setDate}
                    format="dd/MM/yyyy"
                    slotProps={{ textField: { fullWidth: true, required: true } }}
                  />
                  <TextField
                    label="Sale Price per Unit"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    required
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: '0.01', inputMode: 'decimal' }}
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    sx={numberInputSx}
                  />
                  <TextField
                    label="Units Sold"
                    value={units}
                    onChange={(e) => setUnits(e.target.value)}
                    required
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: 'any', inputMode: 'decimal' }}
                    error={Boolean(preview?.shortBy)}
                    helperText={preview?.shortBy
                      ? `More than you own: reduce by ${preview.shortBy.toFixed(2)}`
                      : 'Changing units updates your holdings, oldest shares first'}
                    sx={numberInputSx}
                  />
                  <TextField
                    label="Charges"
                    value={charges}
                    onChange={(e) => setCharges(e.target.value)}
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: '0.01', inputMode: 'decimal' }}
                    helperText="Brokerage, taxes and other fees"
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    sx={numberInputSx}
                  />
                  {preview && !preview.shortBy && (
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Net received</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatINR(preview.net)}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary">Profit/Loss</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: preview.profitLoss >= 0 ? 'success.main' : 'error.main' }}>
                          {preview.profitLoss >= 0 ? '+' : '−'}{formatINR(Math.abs(preview.profitLoss))}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </LocalizationProvider>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!sale || saving || Boolean(preview?.shortBy)}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Saving...' : 'Update Sale'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSaleDialog;
